import {
  AUCTION_MAX_LISTINGS,
  type BagEntry,
  carriedEntries,
  getItem,
  isAuctionable,
  type ItemKind,
  type ItemTemplate,
} from '@shefira/shared';

import { AUCTION_SLOT_LABELS, type AuctionDesk, KIND_LABELS } from './auction-format.js';
import { AuctionSellForm } from './auction-sell-form.js';
import { filterChips, itemRow, markIcon, upgradedName } from './bench.js';
import { describeOnHover, hideItemHover } from './item-hover.js';
import { uiFormat, uiText } from './text.js';

/**
 * « Vendre » (ADR 0035), in the window itself: the bag's items the house
 * takes, as small cards, narrowed by kind; « Vendre » on one opens the form
 * (quantity, price, the fee and what comes back). The bag no longer opens
 * beside the window in pick mode: a phone had no room for both. What may be
 * listed is what the server accepts, `isAuctionable` (unbound, past a shop
 * purchase's cooldown), from the bag or the wardrobe.
 */

export interface AuctionSellOptions {
  /** My active lots, against `AUCTION_MAX_LISTINGS`. */
  activeLots: () => number;
  onListed: () => void;
}

type Filter = 'all' | ItemKind;

const FILTERS: readonly Filter[] = ['all', 'equipment', 'material', 'consumable'];

/** Pieces first, the lots the house is for; then the loot, then supplies. */
const KIND_ORDER: Record<ItemKind, number> = { equipment: 0, material: 1, consumable: 2 };

interface Listable {
  entry: BagEntry;
  template: ItemTemplate;
}

export class AuctionSellView {
  readonly root: HTMLElement;
  private readonly body: HTMLElement;
  private readonly form: AuctionSellForm;
  private filter: Filter = 'all';
  /** The bag item whose form is open. */
  private picked: string | undefined;

  constructor(
    private readonly desk: AuctionDesk,
    private readonly options: AuctionSellOptions,
  ) {
    this.root = document.createElement('div');
    this.root.className = 'auction__view auction__view--sell';
    this.body = document.createElement('div');
    this.body.className = 'bench__list auction__list';
    this.form = new AuctionSellForm(desk, {
      activeLots: options.activeLots,
      onBack: () => {
        this.back();
      },
      onListed: () => {
        this.picked = undefined;
        this.options.onListed();
        this.render();
      },
    });
    this.root.append(this.body);
  }

  /** Draws the side again from the bag as the room last pushed it. */
  render(): void {
    if (this.root.hidden) return;
    hideItemHover();
    const listable = this.listable();
    const picked = listable.find(({ entry }) => entry.item.id === this.picked);
    // Sold elsewhere, or gone from the bag: back to the list.
    if (picked === undefined) this.picked = undefined;
    this.root.classList.toggle('auction__view--form', picked !== undefined);
    if (picked !== undefined) {
      this.form.show(picked.entry, picked.template);
      this.body.replaceChildren(this.form.root);
      return;
    }
    const scroll = this.body.scrollTop;
    this.body.replaceChildren(...this.listNodes(listable));
    this.body.scrollTop = scroll;
  }

  /** Out of the form, if it was open: whether there was one to leave. */
  back(): boolean {
    if (this.picked === undefined) return false;
    this.picked = undefined;
    this.form.reset();
    this.render();
    return true;
  }

  destroy(): void {
    this.form.reset();
  }

  private listable(): Listable[] {
    const inventory = this.desk.inventory();
    if (inventory === undefined) return [];
    const found: Listable[] = [];
    for (const entry of carriedEntries(inventory)) {
      const template = getItem(entry.item.templateId);
      if (template !== undefined && isAuctionable(entry.item)) found.push({ entry, template });
    }
    return found.sort(
      (a, b) =>
        KIND_ORDER[a.template.kind] - KIND_ORDER[b.template.kind] || a.entry.slot - b.entry.slot,
    );
  }

  private listNodes(listable: readonly Listable[]): Node[] {
    const nodes: Node[] = [];
    const inventory = this.desk.inventory();
    const kept = inventory === undefined ? 0 : carriedEntries(inventory).length - listable.length;
    const full = this.options.activeLots() >= AUCTION_MAX_LISTINGS;
    if (full) {
      nodes.push(
        this.hint(
          uiFormat('Tu as déjà {max} lots en vente : retires-en un d’abord.', {
            max: AUCTION_MAX_LISTINGS,
          }),
          true,
        ),
      );
    }
    if (listable.length === 0) {
      nodes.push(this.hint(uiText('Rien dans ton sac ne peut être mis en vente.')));
      return nodes;
    }
    if (this.filter !== 'all' && !listable.some(({ template }) => template.kind === this.filter)) {
      this.filter = 'all';
    }
    const labels: Record<Filter, string> = {
      all: uiText('Tout'),
      equipment: uiText('Équipement'),
      material: uiText('Matériaux'),
      consumable: uiText('Consommables'),
    };
    const chips = FILTERS.map((filter) => ({
      value: filter,
      label: labels[filter],
      count: listable.filter(({ template }) => filter === 'all' || template.kind === filter).length,
    })).filter((chip) => chip.value === 'all' || chip.count > 0);
    nodes.push(
      filterChips(chips, this.filter, (filter) => {
        this.filter = filter;
        this.render();
      }),
    );
    for (const item of listable) {
      if (this.filter === 'all' || item.template.kind === this.filter) {
        nodes.push(this.row(item, full));
      }
    }
    // Why some of the bag is missing here, told once at the foot of the list.
    if (kept > 0) {
      nodes.push(
        this.hint(
          kept === 1
            ? uiText('Un objet lié ou acheté il y a moins de 14 jours reste dans ton sac.')
            : uiFormat(
                '{count} objets liés ou achetés il y a moins de 14 jours restent dans ton sac.',
                { count: kept },
              ),
        ),
      );
    }
    return nodes;
  }

  private hint(text: string, warn = false): HTMLElement {
    const hint = document.createElement('p');
    hint.className = warn ? 'smith__warn auction__warn' : 'bench__hint auction__hint';
    hint.textContent = text;
    return hint;
  }

  private row({ entry, template }: Listable, full: boolean): HTMLElement {
    const { item } = entry;
    const parts = itemRow(
      template,
      this.desk.icons,
      upgradedName(template, item.templateId, item.upgradeLevel),
    );
    parts.row.classList.add('auction__row');
    parts.row.dataset.item = item.id;
    markIcon(parts.icon, item.quantity, item.upgradeLevel);
    const slot = template.slot === undefined ? undefined : AUCTION_SLOT_LABELS[template.slot];
    parts.meta.textContent = uiText(slot ?? KIND_LABELS[template.kind]);
    const context = () => ({ wearer: this.desk.wearer(), details: {}, instance: item });
    describeOnHover(parts.slot, template, context);
    describeOnHover(parts.text, template, context);

    const pick = document.createElement('button');
    pick.type = 'button';
    pick.className = 'smith__go auction__pick';
    pick.textContent = uiText('Vendre');
    pick.disabled = full;
    pick.addEventListener('click', () => {
      this.picked = item.id;
      this.form.reset();
      this.render();
      this.form.focus();
    });
    parts.actions.append(pick);
    return parts.row;
  }
}
